import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, staggerChild } from '../../ui/animations';
import { calculateHarvestValue, getQualityTier } from '../../game/systems/economics';

const CHANNELS = ['wholesale', 'medical', 'retail', 'blackmarket'];
const CHANNEL_COLORS = { wholesale: 'var(--blue)', medical: 'var(--green)', retail: 'var(--yellow)', blackmarket: 'var(--red)' };
const CHANNEL_ICONS  = { wholesale: '📦', medical: '🏥', retail: '🏪', blackmarket: '🚫' };

function isChannelAvailable(channel, licenseLevel, unlockedTech) {
  if (channel === 'wholesale') return true; // always available; license provides compliance protection
  if (channel === 'medical')   return licenseLevel >= 2 && unlockedTech.includes('D3');
  if (channel === 'retail')    return licenseLevel >= 3 && unlockedTech.includes('D2');
  if (channel === 'blackmarket') return true; // always available (illegal)
  return false;
}

export function MarketScreen({ state, dispatch }) {
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState('wholesale');
  const [sellQty, setSellQty] = useState(0);

  const freshInventory = state.inventory.filter(i =>
    i.status !== 'sold' && i.status !== 'in_processing' && i.status !== 'in_curing' && i.grams > 0
  );

  const selectedItem = freshInventory.find(i => i.id === selectedInventory);
  const { pricePerGram, tier: selectedTier, baseChannelPrice } = selectedItem
    ? calculateHarvestValue(selectedItem, selectedChannel, state.reputation, state.marketPrices, state.supplyGlut, state.unlockedTech)
    : { pricePerGram: 0, tier: null, baseChannelPrice: 0 };

  const activeContractsByChannel = state.activeContracts.filter(c => c.status === 'active');

  return (
    <div className="screen" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 14 }}>

      {/* LEFT — Market prices + inventory */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Price ticker */}
        <div className="panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div className="panel-title">Current Prices</div>
            <span className={`alert-pill ${state.supplyGlut ? 'warning' : 'success'}`}>
              {state.supplyGlut ? '📉 Supply Glut' : `📈 ${state.marketTrend}`}
            </span>
          </div>
          <motion.div
            className="price-ticker"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {CHANNELS.map(ch => {
              const available = isChannelAvailable(ch, state.licenseLevel, state.unlockedTech);
              return (
                <motion.div key={ch} variants={staggerChild}>
                  <div className={`price-card ${ch === 'blackmarket' ? 'illegal' : ''}`}
                    style={{ opacity: available ? 1 : 0.4, borderColor: available ? CHANNEL_COLORS[ch] : undefined }}>
                    <div className="channel-label">{CHANNEL_ICONS[ch]} {ch}</div>
                    <div className="channel-price" style={{ color: CHANNEL_COLORS[ch] }}>
                      ${state.marketPrices[ch]?.toFixed(2)}/g
                    </div>
                    <div className={`channel-trend ${state.marketTrend === 'rising' ? 'up' : state.marketTrend === 'falling' ? 'down' : 'flat'}`}>
                      {state.marketTrend === 'rising' ? '▲' : state.marketTrend === 'falling' ? '▼' : '—'} {state.marketTrend}
                    </div>
                    {!available && <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 2 }}>
                      {ch === 'medical' ? 'Needs T2 License + CRM' : ch === 'retail' ? 'Needs T3 License' : ''}
                    </div>}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Licenses */}
        <div className="panel">
          <div className="panel-title">Licensing</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { tier: 1, label: 'Grower License', cost: 2500, desc: 'Compliance protection, reduces inspection fines' },
              { tier: 2, label: 'Processor License', cost: 5000, desc: 'Enables medical sales' },
              { tier: 3, label: 'Retailer License', cost: 15000, desc: 'Enables retail + brand' },
            ].map(({ tier, label, cost, desc }) => (
              <div key={tier} style={{
                background: state.licenseLevel >= tier ? 'var(--green-dark)' : 'var(--border-light)',
                border: `1px solid ${state.licenseLevel >= tier ? 'var(--green-dim)' : 'var(--border)'}`,
                borderRadius: 'var(--radius)',
                padding: '8px 12px',
                flex: '1 0 160px',
              }}>
                <div style={{ fontWeight: 700, fontSize: 12 }}>
                  {state.licenseLevel >= tier ? '✅ ' : ''}{label}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', margin: '3px 0' }}>{desc}</div>
                {state.licenseLevel < tier && (
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ marginTop: 6 }}
                    disabled={state.cash < cost || tier > state.licenseLevel + 1}
                    onClick={() => dispatch({ type: 'BUY_LICENSE', payload: { tier } })}
                  >
                    ${cost.toLocaleString()}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Inventory */}
        <div className="panel">
          <div className="panel-title">Inventory ({freshInventory.length} batches · {freshInventory.reduce((s, i) => s + i.grams, 0)}g total)</div>
          {freshInventory.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📦</span>
              <span className="empty-title">No Product</span>
              <span className="empty-desc">Harvest a batch to create sellable inventory.</span>
            </div>
          ) : (
            <div>
              {freshInventory.map(item => {
                const isSelected = item.id === selectedInventory;
                const itemTier = getQualityTier(item.qualityScore);
                return (
                  <motion.div
                    key={item.id}
                    onClick={() => { setSelectedInventory(item.id); setSellQty(item.grams); }}
                    whileHover={{ x: 2, backgroundColor: 'rgba(28,35,48,1)' }}
                    transition={{ duration: 0.1 }}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 55px 100px 70px',
                      gap: 8,
                      alignItems: 'center',
                      padding: '8px',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      border: `1px solid ${isSelected ? 'var(--green)' : 'transparent'}`,
                      background: isSelected ? 'var(--green-dark)' : 'transparent',
                      marginBottom: 2,
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{item.strainId.replace(/-/g, ' ')}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>Day {item.harvestDay}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{item.grams}g</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                      <span style={{ background: itemTier.color + '22', color: itemTier.color, padding: '1px 5px', borderRadius: 3, fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap' }}>
                        {itemTier.emoji} {itemTier.label}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{item.qualityScore}</span>
                      {(item.type ?? 'flower') === 'extract' && (
                        <span style={{ background: 'rgba(180,71,255,0.15)', color: '#b347ff', padding: '1px 5px', borderRadius: 3, fontWeight: 700, fontSize: 10 }}>
                          💎 {item.extractType ?? 'Extract'}
                        </span>
                      )}
                      {item.packaged && (
                        <span style={{ background: 'rgba(63,185,80,0.15)', color: 'var(--green)', padding: '1px 5px', borderRadius: 3, fontWeight: 700, fontSize: 10 }}>
                          📦 Packaged
                        </span>
                      )}
                      {item.status === 'cured' && (
                        <span style={{ background: 'rgba(88,166,255,0.15)', color: 'var(--blue)', padding: '1px 5px', borderRadius: 3, fontWeight: 700, fontSize: 10 }}>
                          ✓ Cured +{item.cureBonus ?? 0}pts
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{item.status}</div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — Sell panel + Contracts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Sell panel */}
        <div className="panel">
          <div className="panel-title">💵 Sell Inventory</div>
          {selectedItem ? (
            <div>
              <div style={{ marginBottom: 10, padding: '8px', background: 'var(--border-light)', borderRadius: 'var(--radius-sm)' }}>
                <div style={{ fontWeight: 700 }}>{selectedItem.strainId.replace(/-/g, ' ')}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{selectedItem.grams}g available · Quality {selectedItem.qualityScore}/100</div>
              </div>

              {/* Channel selector */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>Sales Channel</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {CHANNELS.map(ch => {
                    const available = isChannelAvailable(ch, state.licenseLevel, state.unlockedTech);
                    const { pricePerGram: ppg } = calculateHarvestValue(
                      selectedItem, ch, state.reputation, state.marketPrices, state.supplyGlut, state.unlockedTech
                    );
                    return (
                      <button
                        key={ch}
                        className={`btn ${selectedChannel === ch ? '' : 'btn-secondary'} btn-sm`}
                        style={{
                          justifyContent: 'space-between',
                          background: selectedChannel === ch ? CHANNEL_COLORS[ch] + '33' : undefined,
                          borderColor: selectedChannel === ch ? CHANNEL_COLORS[ch] : undefined,
                          color: available ? 'var(--text)' : 'var(--text-dim)',
                          opacity: available ? 1 : 0.4,
                        }}
                        disabled={!available}
                        onClick={() => setSelectedChannel(ch)}
                      >
                        <span>{CHANNEL_ICONS[ch]} {ch}</span>
                        <span style={{ color: CHANNEL_COLORS[ch] }}>${ppg.toFixed(2)}/g</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quantity */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Quantity: {sellQty}g</div>
                <input
                  type="range" min={1} max={selectedItem.grams} step={1} value={sellQty}
                  onChange={e => setSellQty(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--green)' }}
                />
              </div>

              <div style={{ background: 'var(--border-light)', borderRadius: 'var(--radius-sm)', padding: '8px', marginBottom: 10 }}>
                {/* Quality tier + bonus row */}
                {selectedTier && (() => {
                  const bonusPct = Math.round((selectedTier.mult - 1) * 100);
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ background: selectedTier.color + '25', color: selectedTier.color, padding: '2px 6px', borderRadius: 4, fontWeight: 700, fontSize: 11 }}>
                          {selectedTier.emoji} {selectedTier.label}
                        </span>
                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Q:{selectedItem.qualityScore}</span>
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: bonusPct >= 55 ? '#b347ff' : bonusPct >= 10 ? 'var(--green)' : 'var(--text-muted)' }}>
                        {bonusPct >= 0 ? '+' : ''}{bonusPct}% quality bonus
                      </span>
                    </div>
                  );
                })()}
                {/* Base vs customer price */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                  <span>Base market price</span>
                  <span>${baseChannelPrice.toFixed(2)}/g</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Customer pays × {sellQty}g</span>
                  <span style={{ fontWeight: 700, color: 'var(--green)', fontSize: 14 }}>${pricePerGram.toFixed(2)}/g → <span style={{ fontSize: 16 }}>${Math.round(pricePerGram * sellQty).toLocaleString()}</span></span>
                </div>
                {selectedChannel === 'blackmarket' && (
                  <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>
                    ⚠ Illegal sale — investigation risk {Math.min(100, (state.illegalSalesCount + 1) * 5)}%
                  </div>
                )}
              </div>

              <motion.button
                className={`btn ${selectedChannel === 'blackmarket' ? 'btn-danger' : 'btn-primary'}`}
                style={{ width: '100%' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  dispatch({ type: 'SELL_HARVEST', payload: { inventoryId: selectedItem.id, channel: selectedChannel, quantity: sellQty } });
                  setSelectedInventory(null);
                }}
              >
                {selectedChannel === 'blackmarket' ? '🚫 Sell (Illegal)' : '💵 Sell Now'}
              </motion.button>
            </div>
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>← Select an inventory item to sell.</div>
          )}
        </div>

        {/* Active contracts */}
        <div className="panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div className="panel-title">Active Contracts</div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{activeContractsByChannel.length} active</span>
          </div>
          {activeContractsByChannel.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No active contracts. Accept one below.</div>
          ) : (
            <motion.div variants={staggerContainer} initial="initial" animate="animate">
              {activeContractsByChannel.map(contract => {
                const daysLeft = contract.deadline - state.gameDay;
                return (
                  <motion.div key={contract.id} variants={staggerChild}>
                    <div className="contract-card">
                      <div className="contract-header">
                        <span className="contract-title">
                          <span className={`channel-dot ${contract.channel}`} />
                          {' '}{contract.label}
                        </span>
                        <span className={`contract-deadline ${daysLeft < 5 ? 'urgent' : ''}`}>
                          {daysLeft > 0 ? `${daysLeft}d left` : 'OVERDUE'}
                        </span>
                      </div>
                      <div style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
                          {contract.gramsDelivered}/{contract.gramsRequired}g delivered
                        </div>
                        <div style={{ background: 'var(--border-light)', borderRadius: 3, height: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${(contract.gramsDelivered / contract.gramsRequired) * 100}%`, background: 'var(--green)', borderRadius: 3 }} />
                        </div>
                      </div>
                      {selectedItem && (
                        <button
                          className="btn btn-primary btn-sm"
                          style={{ width: '100%' }}
                          disabled={!selectedItem || selectedItem.qualityScore < contract.qualityFloor}
                          onClick={() => dispatch({ type: 'FULFILL_CONTRACT', payload: { contractId: contract.id, inventoryId: selectedItem.id, grams: Math.min(selectedItem.grams, contract.gramsRequired - contract.gramsDelivered) } })}
                        >
                          Deliver {Math.min(selectedItem?.grams || 0, contract.gramsRequired - contract.gramsDelivered)}g
                          {selectedItem?.qualityScore < contract.qualityFloor && ' (quality too low)'}
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>

        {/* Available contracts */}
        <div className="panel">
          <div className="panel-title">Available Contracts</div>
          {state.availableContracts.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>New contracts appear weekly.</div>
          ) : (
            <motion.div variants={staggerContainer} initial="initial" animate="animate">
              {state.availableContracts.map(contract => (
                <motion.div key={contract.id} variants={staggerChild}>
                  <div className="contract-card">
                    <div className="contract-header">
                      <span className="contract-title">
                        <span className={`channel-dot ${contract.channel}`} />
                        {' '}{contract.label}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{contract.durationDays}d</span>
                    </div>
                    <div className="contract-details">
                      <span>📦 {contract.gramsRequired}g</span>
                      <span>⭐ Q≥{contract.qualityFloor}</span>
                      <span style={{ color: CHANNEL_COLORS[contract.channel] }}>
                        {(contract.priceMultiplier * 100).toFixed(0)}% rate
                      </span>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ marginTop: 8, width: '100%' }}
                      disabled={!isChannelAvailable(contract.channel, state.licenseLevel, state.unlockedTech)}
                      onClick={() => dispatch({ type: 'ACCEPT_CONTRACT', payload: { contractId: contract.id } })}
                    >
                      Accept Contract
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>

        {/* Industry Reviews */}
        <div className="panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div className="panel-title">📰 Industry Reviews</div>
            {(state.pendingReviews || []).length > 0 && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {state.pendingReviews.length} pending…
              </span>
            )}
          </div>
          {(state.publishedReviews || []).length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              Sell 50g+ via wholesale, medical, or retail to attract critic attention. Reviews publish 7 days after a drop.
            </div>
          ) : (
            (state.publishedReviews || []).slice(0, 6).map(review => {
              const scoreColor = review.score >= 80 ? 'var(--green)' : review.score >= 60 ? 'var(--yellow)' : 'var(--red)';
              return (
                <div key={review.id} style={{
                  marginBottom: 10,
                  padding: '10px',
                  background: 'var(--border-light)',
                  borderRadius: 'var(--radius-sm)',
                  borderLeft: `3px solid ${scoreColor}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 5 }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{review.reviewerEmoji} {review.reviewerName}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>{review.reviewerHandle}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{review.score}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>/100</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text)', fontStyle: 'italic', marginBottom: 5 }}>
                    "{review.quote}"
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)' }}>
                    <span>Day {review.day} · {review.channel}</span>
                    <span style={{ color: review.repImpact >= 0 ? 'var(--green)' : 'var(--red)' }}>
                      {review.repImpact >= 0 ? '+' : ''}{review.repImpact} rep
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
